import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import { TooltipPortal } from "../TooltipModal/TooltipPortal";
import { TwoColumGrid } from "../../layout/portalPageLayout";
import { screenSize } from "../../theme";
import type {
  AttributeGroupSummary,
  SupportedAssetAccessibility,
} from "../../types/types.PIM";
import { AttributeValueDisplay } from "../../pages/SharedPages/ProductDetailPage/ProductOverview/product_overview.utils";
import { shouldShowObjectReferenceAttribute } from "../ObjectReference/utils";
import { useStoreState } from "../../util/util";

export type AttributeGroupDetailsConfig = {
  group: AttributeGroupSummary;
  isPortfolio?: boolean;
  showMandatoryAsterisk?: boolean;
  shouldHideEmptyAttributes?: boolean;
  disableFiltering?: boolean;
  roleIsGuest?: boolean;
  roleIsSomeKindOfBuyer?: boolean;
  roleIsSomeKindOfSeller?: boolean;
};

const InnerTitleText = styled.p`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  display: inline-block;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin: ${({ theme }) => theme.resolve("spacing_xl")} 0
    ${({ theme }) => theme.resolve("spacing_xxs")};
`;

const SingleColumnSpan = styled.div`
  grid-column: span 1;
`;

const TwoColumnSpan = styled.div`
  grid-column: span 2;
  @media ${screenSize.small} {
    grid-column: span 1;
  }
`;

/**
 * Shared component for displaying attribute group details in read-only mode.
 * Used by both ProductGroupCard and EntityGroupCard to ensure identical appearance.
 */
export function AttributeGroupDetails({
  group,
  isPortfolio = false,
  showMandatoryAsterisk = false,
  shouldHideEmptyAttributes = false,
  disableFiltering = false,
  roleIsGuest = false,
  roleIsSomeKindOfBuyer = false,
  roleIsSomeKindOfSeller = false,
}: AttributeGroupDetailsConfig): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();

  const entirelySelectAttributes = group.attributes.every(
    (attr) =>
      (attr.input_type === "single_select" ||
        attr.input_type === "multi_select") &&
      attr.icons_enabled
  );

  const entirelyImageAttributes = group.attributes.every(
    (attr) => attr.input_type === "image"
  );

  const entirelyDocumentAttributes = group.attributes.every(
    (attr) => attr.input_type === "document"
  );

  const content = group.attributes.reduce<JSX.Element[]>((acc, attribute) => {
    if (
      !shouldShowObjectReferenceAttribute({
        inputType: attribute.input_type,
        objectReferenceEnabled: object_reference_enabled,
      })
    ) {
      return acc;
    }

    // Skip filtering entirely for entities (disableFiltering=true)
    if (!disableFiltering) {
      const is_not_accessible = (
        accessibility?: SupportedAssetAccessibility
      ) => {
        return roleIsGuest
          ? accessibility !== "public"
          : roleIsSomeKindOfBuyer
          ? accessibility === "internal"
          : false;
      };

      const hasDocumentValues = attribute.values?.some(
        (entry) => (entry.value as { asset_id?: string } | null)?.asset_id
      );

      const hasObjectReferenceValues =
        attribute.input_type === "object_reference" &&
        attribute.values?.some((entry) => entry.object_ref?.id);

      const shouldSkip =
        (shouldHideEmptyAttributes &&
          ((attribute.input_type === "object_reference" &&
            !hasObjectReferenceValues) ||
            (attribute.input_type !== "document" &&
              attribute.input_type !== "toggle" &&
              attribute.input_type !== "object_reference" &&
              !attribute.values?.[0]?.value) ||
            (attribute.input_type === "document" && !hasDocumentValues) ||
            ((attribute.input_type === "image" ||
              attribute.input_type === "document") &&
              is_not_accessible(
                attribute.values?.[0]?.visibility ?? "public"
              )) ||
            attribute.is_not_applicable ||
            (attribute.is_restricted && roleIsGuest) ||
            !attribute.is_visible)) ||
        (!shouldHideEmptyAttributes && !attribute.is_visible);

      if (shouldSkip) {
        return acc;
      }
    }

    // Determine column span for product reference / object-reference cards
    const isObjectRefProductCard =
      attribute.input_type === "object_reference" &&
      (attribute.target_object_type ?? "product") === "product" &&
      "display_configuration" in attribute &&
      attribute.display_configuration === "product_card";

    const isProductReferenceCard =
      (attribute.input_type === "product_reference" &&
        "display_configuration" in attribute &&
        attribute.display_configuration === "product_card") ||
      isObjectRefProductCard;

    const isMultipleReferences =
      isProductReferenceCard && attribute.values && attribute.values.length > 1;

    const attributeContent = (
      <div key={attribute.id}>
        <InnerTitleText
          data-for={attribute.id}
          data-tip={
            roleIsSomeKindOfSeller && attribute.description
              ? attribute.description
              : undefined
          }
        >
          {attribute.display_name
            ? t([attribute.display_name]) +
              (attribute.is_mandatory && !isPortfolio && showMandatoryAsterisk
                ? " *"
                : "")
            : attribute.name +
              (attribute.is_mandatory && !isPortfolio && showMandatoryAsterisk
                ? " *"
                : "")}
        </InnerTitleText>
        <AttributeValueDisplay
          attribute={attribute}
          isPortfolio={isPortfolio}
        />
        {roleIsSomeKindOfSeller && attribute.description?.replace(/\s/g, "") ? (
          <TooltipPortal>
            <ReactTooltip
              id={attribute.id}
              place="top"
              data-html={true}
              effect="solid"
              backgroundColor={theme.tooltipBG}
              multiline={true}
            />
          </TooltipPortal>
        ) : (
          <></>
        )}
      </div>
    );

    // Wrap with appropriate column span
    const wrappedContent = isMultipleReferences ? (
      <TwoColumnSpan key={attribute.id}>{attributeContent}</TwoColumnSpan>
    ) : isProductReferenceCard ? (
      <SingleColumnSpan key={attribute.id}>{attributeContent}</SingleColumnSpan>
    ) : (
      attributeContent
    );

    acc.push(wrappedContent);
    return acc;
  }, []);

  return entirelySelectAttributes ||
    entirelyImageAttributes ||
    entirelyDocumentAttributes ? (
    <div>{content}</div>
  ) : (
    <TwoColumGrid>{content}</TwoColumGrid>
  );
}
